import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { grey, orange, red, yellow, blue } from '@mui/material/colors'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useState } from 'react'

const TEXT_DARK = 'rgba(0, 0, 0, 0.87)'
/** Altezza fissa desktop: chiuso e aperto devono coincidere. */
const DESKTOP_BAR_HEIGHT = 64
/** Apertura/chiusura summary (desktop orizzontale, mobile verticale). */
const SUMMARY_ANIMATION_MS = 280

/* ─── Single stat cell ─────────────────────────────────────────────────────── */
const StatCell = ({ label, value, icon: Icon, iconColor, isLast, compact }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: compact ? 52 : 60,
      height: '100%',
      px: compact ? 0.5 : 0.65,
      py: 0,
      borderRight: isLast ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
      textAlign: 'center',
      flexShrink: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.05 }}>
      <Typography
        variant='caption'
        sx={{
          color: TEXT_DARK,
          fontWeight: 600,
          fontSize: compact ? '0.55rem' : '0.62rem',
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Icon sx={{ fontSize: compact ? 11 : 13, color: iconColor, flexShrink: 0 }} />
    </Box>
    <Typography
      component='span'
      sx={{
        fontWeight: 700,
        lineHeight: 1.1,
        color: TEXT_DARK,
        fontSize: compact ? '0.78rem' : '0.88rem',
      }}
    >
      {value}
    </Typography>
  </Box>
)

/* ─── Vertical group label (desktop) — no CSS border (rotate flips edges) ─── */
const GroupLabel = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
      px: 0.4,
      writingMode: 'vertical-rl',
      textOrientation: 'mixed',
      transform: 'rotate(180deg)',
      flexShrink: 0,
      boxSizing: 'border-box',
    }}
  >
    <Typography
      variant='caption'
      sx={{
        color: TEXT_DARK,
        fontWeight: 700,
        fontSize: '0.6rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  </Box>
)

/* ─── Horizontal group label (mobile/tablet) ───────────────────────────────── */
const RowGroupLabel = ({ children }) => (
  <Box
    sx={{
      px: 0.75,
      py: 0.25,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      background: 'rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Typography
      variant='caption'
      sx={{
        color: TEXT_DARK,
        fontWeight: 700,
        fontSize: '0.58rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  </Box>
)

/* ─── One scrollable row of cells ──────────────────────────────────────────── */
const CellRow = ({ rows, data, compact }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: 2 },
    }}
  >
    {rows.map((row, i) => (
      <StatCell
        key={row.key}
        label={row.label}
        value={data[row.key] ?? 0}
        icon={row.icon}
        iconColor={row.color}
        isLast={i === rows.length - 1}
        compact={compact}
      />
    ))}
  </Box>
)

/* ─── Pill shown when collapsed (mobile) ───────────────────────────────────── */
const CollapsedPill = ({ total, totalPeople, onExpand }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.6,
      cursor: 'pointer',
    }}
    onClick={onExpand}
  >
    <DirectionsCarIcon sx={{ fontSize: 15, color: grey[700] }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: TEXT_DARK }}>{total}</Typography>
    <Box sx={{ width: 1, height: 20, background: (theme) => theme.palette.divider }} />
    <PeopleIcon sx={{ fontSize: 15, color: blue[700] }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: TEXT_DARK }}>{totalPeople}</Typography>
    <ExpandMoreIcon sx={{ fontSize: 16, color: grey[600], ml: 0.5 }} />
  </Box>
)

/* ─── Main component ────────────────────────────────────────────────────────── */
export default function GeneralInfo({ crashesSummary }) {
  const { _ } = useLingui()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isTablet  = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  const [expanded, setExpanded] = useState(true)

  const s = crashesSummary ?? {}

  const crashRows = [
    { key: 'total',                label: _(msg`Total`),   icon: DirectionsCarIcon, color: grey[700] },
    { key: 'slightInjurySeverity', label: _(msg`Slight`),  icon: DirectionsCarIcon, color: yellow.A700 },
    { key: 'seriousInjurySeverity',label: _(msg`Serious`), icon: DirectionsCarIcon, color: orange[800] },
    { key: 'fatalSeverity',        label: _(msg`Fatal`),   icon: DirectionsCarIcon, color: red.A700 },
  ]

  const peopleRows = [
    { key: 'totalPeople',              label: _(msg`Total`),   icon: PeopleIcon,  color: blue[700] },
    { key: 'slightInjurySeverityPeople', label: _(msg`Slight`),icon: PersonIcon,  color: yellow.A700 },
    { key: 'seriousInjurySeverityPeople',label: _(msg`Serious`),icon: PersonIcon, color: orange[800] },
    { key: 'fatalSeverityPeople',      label: _(msg`Fatal`),   icon: PersonIcon,  color: red.A700 },
  ]

  const containerBase = {
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 2,
    boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
    position: 'absolute',
    left: '50%',
    bottom: '1.5rem',
    transform: 'translateX(-50%)',
    zIndex: (theme) => theme.zIndex.drawer - 1,
    overflow: 'hidden',
  }

  /* ── Desktop: collapsible horizontal row ─────────────────────────────────── */
  if (isDesktop) {
    return (
      <Box
        sx={{
          ...containerBase,
          display: 'inline-flex',
          alignItems: 'stretch',
          height: DESKTOP_BAR_HEIGHT,
          minHeight: DESKTOP_BAR_HEIGHT,
          maxHeight: DESKTOP_BAR_HEIGHT,
          boxSizing: 'border-box',
          transition: `box-shadow ${SUMMARY_ANIMATION_MS}ms ease`,
        }}
      >
        <GroupLabel>
          {expanded ? _(msg`Crashes`) : _(msg`Summary`)}
        </GroupLabel>
        {expanded && (
          <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(0, 0, 0, 0.12)', flexShrink: 0 }} />
        )}

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'stretch',
            height: '100%',
            maxWidth: expanded ? 900 : 0,
            opacity: expanded ? 1 : 0,
            overflow: 'hidden',
            transition: `max-width ${SUMMARY_ANIMATION_MS}ms ease, opacity ${SUMMARY_ANIMATION_MS * 0.75}ms ease`,
          }}
        >
          {crashRows.map((row) => (
            <StatCell
              key={row.key}
              label={row.label}
              value={s[row.key] ?? 0}
              icon={row.icon}
              iconColor={row.color}
              isLast={false}
            />
          ))}
          <Box sx={{ width: 3, background: 'rgba(0, 0, 0, 0.12)', flexShrink: 0 }} />
          <GroupLabel>{_(msg`People`)}</GroupLabel>
          <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(0, 0, 0, 0.12)', flexShrink: 0 }} />
          {peopleRows.map((row, i) => (
            <StatCell
              key={row.key}
              label={row.label}
              value={s[row.key] ?? 0}
              icon={row.icon}
              iconColor={row.color}
              isLast={i === peopleRows.length - 1}
            />
          ))}
        </Box>

        <IconButton
          size='small'
          aria-label={expanded ? _(msg`Collapse summary`) : _(msg`Expand summary`)}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            alignSelf: 'center',
            mx: 0.35,
            width: 32,
            height: 32,
            p: 0.25,
            color: TEXT_DARK,
            borderRadius: 1,
            border: expanded ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
            transition: `border-color ${SUMMARY_ANIMATION_MS}ms ease, transform ${SUMMARY_ANIMATION_MS}ms ease`,
            '&:hover': { transform: 'scale(1.06)' },
          }}
        >
          {expanded ? (
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>
    )
  }

  /* ── Tablet: two stacked scrollable rows ─────────────────────────────────── */
  if (isTablet) {
    return (
      <Box sx={{ ...containerBase, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 'calc(100vw - 2rem)' }}>
        <RowGroupLabel>{_(msg`Crashes`)}</RowGroupLabel>
        <CellRow rows={crashRows} data={s} compact />
        <Box sx={{ height: 2, background: (theme) => theme.palette.divider }} />
        <RowGroupLabel>{_(msg`People`)}</RowGroupLabel>
        <CellRow rows={peopleRows} data={s} compact />
      </Box>
    )
  }

  /* ── Mobile: collapsed pill → expand to stacked rows ────────────────────── */
  return (
    <Box sx={{ ...containerBase, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 'calc(100vw - 2rem)' }}>
      {expanded ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1, pt: 0.5, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: TEXT_DARK,
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {_(msg`Summary`)}
            </Typography>
            <IconButton size='small' onClick={() => setExpanded(false)} sx={{ p: 0.25 }}>
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <RowGroupLabel>{_(msg`Crashes`)}</RowGroupLabel>
          <CellRow rows={crashRows} data={s} compact />
          <Box sx={{ height: 2, background: 'rgba(0, 0, 0, 0.12)' }} />
          <RowGroupLabel>{_(msg`People`)}</RowGroupLabel>
          <CellRow rows={peopleRows} data={s} compact />
        </>
      ) : (
        <CollapsedPill
          total={s.total ?? 0}
          totalPeople={s.totalPeople ?? 0}
          onExpand={() => setExpanded(true)}
        />
      )}
    </Box>
  )
}
